import { Bot, CheckCircle, TrendingUp } from 'lucide-react-native';
import { type ReactNode, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { PulseAnimation } from '../../../components/animations/PulseAnimation';
import { SkeletonCard } from '../../../components/animations/Skeleton';
import { Badge, Box, HStack, Text } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import type { AgentConfig, AgentExecution, QueueStatus } from '../types';

interface AgentStatusDashboardProps {
  config?: AgentConfig;
  queueStatus?: QueueStatus;
  executions?: AgentExecution[];
  isLoading: boolean;
}

function isToday(dateString: string): boolean {
  const date = new Date(dateString);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

function StatCell({
  icon,
  value,
  label,
}: Readonly<{
  icon: ReactNode;
  value: string;
  label: string;
}>) {
  return (
    <View style={styles.statCell}>
      <View style={styles.statHeader}>
        {icon}
        <Text style={styles.statValue} numberOfLines={1}>
          {value}
        </Text>
      </View>
      <Text style={styles.statLabel} numberOfLines={2}>
        {label}
      </Text>
    </View>
  );
}

export function AgentStatusDashboard({
  config,
  queueStatus,
  executions,
  isLoading,
}: Readonly<AgentStatusDashboardProps>) {
  const { t } = useTranslation();

  const todayStats = useMemo(() => {
    if (!executions) return { total: 0, successful: 0, rate: 0 };
    const todayExecutions = executions.filter((e) => isToday(e.createdAt));
    const total = todayExecutions.length;
    const successful = todayExecutions.filter((e) => e.status === 'completed').length;
    const rate = total > 0 ? Math.round((successful / total) * 100) : 0;
    return { total, successful, rate };
  }, [executions]);

  const healthStatus = useMemo(() => {
    if (!config?.isActive) return { label: t('agents.status.paused'), color: 'neutral' as const };
    if (queueStatus?.isProcessing)
      return { label: t('agents.status.processing'), color: 'amber' as const };
    return { label: t('agents.status.active'), color: 'green' as const };
  }, [config, queueStatus, t]);

  if (isLoading) {
    return (
      <View style={styles.card}>
        <View style={styles.header}>
          <SkeletonCard
            showIcon={false}
            showDescription={false}
            style={{ width: 96, padding: tokens.spacing.sm }}
          />
        </View>
        <View style={styles.statsRow}>
          {[0, 1, 2].map((i) => (
            <Box key={i} style={{ flex: 1 }}>
              <SkeletonCard style={{ minHeight: 72 }} />
            </Box>
          ))}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <Text variant="h5" color={colors.text.primary} style={styles.headerTitle}>
          {t('agents.status.executionsToday')}
        </Text>
        <HStack spacing="xs" align="center">
          {queueStatus && queueStatus.queueSize > 0 && (
            <Badge color="amber" variant="outline" size="sm">
              {t('agents.status.queued', { count: queueStatus.queueSize })}
            </Badge>
          )}
          {healthStatus.label === 'Processing' ? (
            <PulseAnimation>
              <Badge color={healthStatus.color} variant="subtle" size="sm">
                {healthStatus.label}
              </Badge>
            </PulseAnimation>
          ) : (
            <Badge color={healthStatus.color} variant="subtle" size="sm">
              {healthStatus.label}
            </Badge>
          )}
        </HStack>
      </View>

      <View style={styles.statsRow}>
        <StatCell
          icon={<Bot size={14} color={colors.primary[400]} strokeWidth={2} />}
          value={String(todayStats.total)}
          label={t('agents.status.executionsToday')}
        />
        <StatCell
          icon={<CheckCircle size={14} color={colors.status.success.main} strokeWidth={2} />}
          value={String(todayStats.successful)}
          label={t('agents.status.successful')}
        />
        <StatCell
          icon={<TrendingUp size={14} color={colors.accent.blue[400]} strokeWidth={2} />}
          value={`${todayStats.rate}%`}
          label={t('agents.status.successRate')}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.default,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
    gap: tokens.spacing.sm,
  },
  headerTitle: {
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  statsRow: {
    flexDirection: 'row',
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
    gap: tokens.spacing.sm,
  },
  statCell: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 2,
    gap: 4,
  },
  statHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statValue: {
    fontFamily: darkPrecisionFontFamilies.metricBold,
    fontSize: tokens.fontSize.xlPlus,
    fontWeight: '600',
    letterSpacing: -0.5,
    lineHeight: 26,
    flexShrink: 1,
    color: colors.text.primary,
  },
  statLabel: {
    fontSize: tokens.fontSize.xs,
    fontWeight: '600',
    color: colors.text.tertiary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    lineHeight: 14,
  },
});
